/**
 * Utility functions for handling the application logo
 */
import { api } from './apiUtils'; // Assuming apiUtils is in the same directory

const DEFAULT_LOGO = '/assets/images/logo-new.svg';


// Cache the logo URL in memory to avoid unnecessary API calls
let cachedLogoUrl: string | null = null;

/**
 * Gets the current logo URL from the database or returns the default logo
 * @returns {Promise<string>} The logo URL to use
 */
export const getLogoUrl = async (): Promise<string> => {
  // If we have a cached logo URL, return it
  if (cachedLogoUrl) {
    return cachedLogoUrl;
  }
  
  try {
        const response = await api.get('/admin/logo'); // Path is already correct for Nginx
    
    // Axios directly gives data in response.data
    if (response.data) {
      const data = response.data;
      if (data && data.logoUrl) {
        cachedLogoUrl = data.logoUrl;
        return data.logoUrl;
      }
    }
    
    return DEFAULT_LOGO;
  } catch (error) {
    console.error('Error fetching logo from database:', error);
    return DEFAULT_LOGO;
  }
};

/**
 * Gets the current logo URL synchronously (for components that can't use async/await)
 * This will return the cached logo or default logo
 * @returns {string} The logo URL to use
 */
export const getLogoUrlSync = (): string => {
  return cachedLogoUrl || DEFAULT_LOGO;
};

/**
 * Saves a logo URL to the database
 * @param {string} logoUrl - The logo URL to save
 * @returns {Promise<{success: boolean, message: string}>} Result of the save operation
 */
export const saveLogoUrl = async (logoUrl: string): Promise<{success: boolean, message: string}> => {
  try {
        const response = await api.post('/admin/logo', { logoUrl });
    // Assuming backend returns a success message or relevant data in response.data upon success
    cachedLogoUrl = logoUrl; // Update cache on successful call
    return { success: true, message: response.data.message || 'Logo saved successfully!' };
  } catch (error: any) {
    console.error('Error saving logo to database:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to save logo.'
    };
  }
};

/**
 * Removes the custom logo and reverts to the default
 * @returns {Promise<{success: boolean, message: string}>} Result of the reset operation
 */
export const resetLogoToDefault = async (): Promise<{success: boolean, message: string}> => {
  try {
        const response = await api.delete('/admin/logo');
    // Assuming backend returns a success message or relevant data in response.data upon success
    cachedLogoUrl = null; // Update cache on successful call
    return { success: true, message: response.data.message || 'Logo reset to default!' };
  } catch (error: any) {
    console.error('Error resetting logo to default:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to reset logo.'
    };
  }
};

export default {
  getLogoUrl,
  getLogoUrlSync,
  saveLogoUrl,
  resetLogoToDefault,
  DEFAULT_LOGO
};
